import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
} from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Bar } from 'react-chartjs-2';
import { useRef, useMemo } from 'react';
import { SESSION_TYPES } from '../../../../../models/session';
import useDragger from '../../../../../hooks/scoreMonitor/useDragger';
import { useTranslation } from 'react-i18next';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  Tooltip,
  annotationPlugin
);

const drawLeftRightArrow = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 140;
  canvas.height = 60;
  const ctx = canvas.getContext('2d');
  ctx.strokeStyle = '#333333';
  ctx.fillStyle = '#333333';
  ctx.lineWidth = 10;

  // Draw horizontal line (shaft)
  ctx.beginPath();
  ctx.moveTo(30, 30);
  ctx.lineTo(100, 30);
  ctx.stroke();

  // Draw left arrowhead
  ctx.beginPath();
  ctx.moveTo(10, 30);
  ctx.lineTo(40, 10);
  ctx.lineTo(40, 50);
  ctx.closePath();
  ctx.fill();

  // Draw right arrowhead
  ctx.beginPath();
  ctx.moveTo(130, 30);
  ctx.lineTo(100, 10);
  ctx.lineTo(100, 50);
  ctx.closePath();
  ctx.fill();

  return canvas;
};

const mapPreselectionThresholdToXValue = (data, preselectionThreshold) => {
  let idx = 0;

  while (idx < data.labels.length - 1) {
    if (parseInt(data.labels[idx]) === preselectionThreshold) {
      return idx - 0.5;
    } else if (
      parseInt(data.labels[idx]) < preselectionThreshold &&
      parseInt(data.labels[idx + 1]) >= preselectionThreshold
    ) {
      return idx + 0.5;
    }

    idx++;
  }

  return null;
};

const BarPlot = ({
  data,
  threshold,
  setThreshold,
  preselectionThreshold = null,
  sessionType,
  awardedEntries = {
    absolute: 0,
    percentage: 0,
  },
  notAwardedEntries = {
    absolute: 0,
    percentage: 0,
  },
}) => {
  const { t } = useTranslation();
  const chartRef = useRef(null);

  const thresholdLabelIndex =
    data.labels.findIndex((label) => label === threshold) - 0.5;

  const handleThresholdChange = (newThreshold) => {
    const label = data.labels[newThreshold + 0.5];
    setThreshold(label);
  };

  const { handleEnter, handleLeave, dragger, triggerRerender } = useDragger(
    chartRef,
    handleThresholdChange,
    data.labels.length
  );

  const options = useMemo(() => {
    const hasData = !data.data.every((value) => value === 0);

    return {
      events: ['mousedown', 'mouseup', 'mousemove', 'mouseout'],
      scales: {
        y: {},
        x: {
          offset: true,
          grid: {
            display: false,
          },
        },
      },
      plugins: {
        datalabels: {
          display: false,
        },
        tooltip: {
          displayColors: false,
          callbacks: {
            title: (context) => {
              return t('scoreMonitor.chartView.chart.barplot.tooltip.title', {
                label: context[0]?.label,
              });
            },
            label: (context) => {
              return t('scoreMonitor.chartView.chart.barplot.tooltip.label', {
                value: context.formattedValue,
              });
            },
          },
        },
        annotation: {
          animations: {
            numbers: {
              properties: ['x', 'x2'],
              duration: 150,
            },
          },
          annotations: {
            threshold: hasData
              ? {
                  type: 'line',
                  borderColor: 'black',
                  borderWidth: 3,
                  scaleID: 'x',
                  value: thresholdLabelIndex,
                  xMin: 0,
                  xMax: 500,
                  enter(ctx) {
                    handleEnter(ctx);
                  },
                  leave(ctx) {
                    handleLeave(ctx);
                  },
                  label: {
                    display: true,
                    content: drawLeftRightArrow,
                    backgroundColor: 'white',
                    borderWidth: 3,
                    borderRadius: '20',
                    borderColor: '#333333',
                    width: '50%',
                    height: '50%',
                    position: 'center',
                  },
                  z: 10,
                }
              : undefined,
            preselectionThreshold:
              hasData && sessionType === SESSION_TYPES.FINAL_JURY
                ? {
                    type: 'line',
                    borderColor: 'grey',
                    borderWidth: 2,
                    borderDash: [4],
                    scaleID: 'x',
                    value: mapPreselectionThresholdToXValue(
                      data,
                      preselectionThreshold
                    ),
                    label: {
                      display: true,
                      content: () => {
                        return t(
                          'scoreMonitor.chartView.chart.barplot.preselectionThreshold',
                          {
                            threshold: preselectionThreshold,
                          }
                        );
                      },
                      position: 'start',
                    },
                    z: 100,
                  }
                : undefined,
            notAwardedEntries: hasData
              ? {
                  type: 'label',
                  backgroundColor: 'white',
                  xValue: data.labels.length * 0.1,
                  yValue:
                    data.data.reduce(
                      (maxValue, curr) => Math.max(maxValue, curr),
                      0
                    ) * 0.8,
                  content: [
                    t(
                      'scoreMonitor.chartView.chart.barplot.awardedLabel.percentage',
                      {
                        percentage: notAwardedEntries.percentage.toFixed(2),
                      }
                    ),
                    t(
                      'scoreMonitor.chartView.chart.barplot.awardedLabel.absolute',
                      {
                        absolute: notAwardedEntries.absolute,
                      }
                    ),
                  ],
                  color: 'rgba(128, 128, 128, 0.9)',
                  font: {
                    size: 24,
                    weight: 'bold',
                  },
                  z: 11,
                }
              : undefined,
            awardedEntries: hasData
              ? {
                  type: 'label',
                  backgroundColor: 'white',
                  xValue: data.labels.length * 0.9,
                  yValue:
                    data.data.reduce(
                      (maxValue, curr) => Math.max(maxValue, curr),
                      0
                    ) * 0.8,
                  content: [
                    t(
                      'scoreMonitor.chartView.chart.barplot.awardedLabel.percentage',
                      {
                        percentage: awardedEntries.percentage.toFixed(2),
                      }
                    ),
                    t(
                      'scoreMonitor.chartView.chart.barplot.awardedLabel.absolute',
                      {
                        absolute: awardedEntries.absolute,
                      }
                    ),
                  ],
                  color: 'rgba(220, 38, 38, 0.9)',
                  font: {
                    size: 24,
                    weight: 'bold',
                  },
                  z: 11,
                }
              : undefined,
            noData: hasData
              ? undefined
              : {
                  type: 'label',
                  content: t('scoreMonitor.chartView.chart.barplot.noData'),
                  position: 'center',
                  font: {
                    size: 24,
                    weight: 'bold',
                  },
                  z: 11,
                },
          },
        },
      },
    };
  }, [triggerRerender, thresholdLabelIndex, data]);

  const getDatasetColors = () => {
    const colors = Array(data.data.length)
      .fill('')
      .map((_, index) =>
        index <= thresholdLabelIndex
          ? 'rgba(128, 128, 128, 0.7)'
          : 'rgba(220, 38, 38, 0.7)'
      );
    return colors;
  };

  return (
    <div>
      <Bar
        data={{
          labels: data.labels,
          datasets: [
            {
              label: 'Score',
              data: data.data,
              backgroundColor: getDatasetColors(),
            },
          ],
        }}
        options={options}
        ref={chartRef}
        plugins={[dragger]}
      />
    </div>
  );
};

export default BarPlot;
